import type { RuntimeEventConfig } from '@/shared/config/approval.mapping';
import type { Participant, ParticipantPage, RawAttribute, RawPax, RawPaxPage } from '@/shared/types/pax';

/**
 * Normalização da resposta da API.
 *
 * Motivo de existir: a API devolve os dados do participante como uma lista de
 * pares `{ atributoId, valor }`. O painel anterior resolvia isso dentro do JSX
 * com `.find(...).valor`, e um participante sem o atributo de e-mail derrubava
 * a renderização da tabela inteira. Aqui a ausência é um `null` explícito e o
 * tipo obriga o componente a tratá-la.
 *
 * A parametrização entra por parâmetro, e não por import: o de-para de grupo vem
 * da API em runtime, então este módulo não consegue resolvê-lo sozinho — o que
 * também o mantém puro e testável sem provider.
 *
 * Nenhum atributo é resolvido em campo nomeado: as colunas da tela saem todas da
 * parametrização, então aqui só se indexa o que veio.
 */

export interface MapperConfig {
  groupByMiceId: RuntimeEventConfig['groupByMiceId'];
}

/**
 * Converte a data de cadastro.
 *
 * O banco guarda `datacadastro` sem timezone e o driver serializa como se fosse
 * UTC. O horário correto é, portanto, o próprio componente "UTC" da string —
 * por isso a formatação usa `timeZone: 'UTC'` em `formatRegisteredAt`, em vez do
 * ajuste manual de +3h que o painel antigo fazia (e que quebra no horário de verão).
 */
function parseRegisteredAt(value: string | null): Date | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Vazio e só-espaços contam como ausência, como antes. */
function normalizeValue(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/**
 * Todos os atributos que vieram, indexados pelo ID. A API já devolve apenas os
 * configurados (colunas + perfil), então não há o que filtrar aqui — e atributo
 * novo na parametrização aparece sem mudança neste módulo.
 */
function toValues(attributes: readonly RawAttribute[]): Record<number, string | null> {
  const values: Record<number, string | null> = {};
  for (const attribute of attributes) {
    if (attribute?.atributoId === undefined || attribute.atributoId === null) continue;
    values[attribute.atributoId] = normalizeValue(attribute.valor);
  }
  return values;
}

export function toParticipant(raw: RawPax, config: MapperConfig): Participant {
  const values = toValues(raw.atributos ?? []);

  return {
    id: raw.id,
    nome: raw.nome?.trim() || 'Sem nome',
    values,
    registeredAt: parseRegisteredAt(raw.dataCadastro),
    group: raw.grupoId === null ? null : (config.groupByMiceId.get(raw.grupoId) ?? null),
  };
}

export function toParticipantPage(raw: RawPaxPage, config: MapperConfig): ParticipantPage {
  return {
    participants: (raw.paxes ?? []).map(pax => toParticipant(pax, config)),
    totalRecords: Number.isFinite(raw.count) ? raw.count : 0,
    totalPages: Number.isFinite(raw.total) && raw.total > 0 ? raw.total : 1,
  };
}
